/**
 * How old somebody is, and whether they are old enough.
 *
 * `plan.md` §6.13. MyRave is 18+, and this is the one place that decides it —
 * the API enforces it, the sign-up form warns before submitting, and a second
 * implementation would eventually disagree with the first about a birthday.
 *
 * ## Why the "today" is an argument
 *
 * Nothing here reads the clock. That is the rule for this whole package (no
 * I/O, no `Date.now()` inside a function), and here it also makes the hard
 * cases testable: the day before an eighteenth birthday, the day of it, and 29
 * February. A function that consulted the clock could only be tested on the
 * days those happened to fall.
 *
 * ## Dates, not instants
 *
 * A birth date is a CALENDAR date — "1990-03-14" — and has no time and no zone.
 * Comparing it as an instant makes somebody's age depend on where the server
 * is, so a person in Tokyo could be eighteen and the same person in Los Angeles
 * not. Both sides are reduced to y/m/d integers and compared as numbers, which
 * is what a person means by "how old are you".
 */

/** The age MyRave requires. One place, so the API and the form agree. */
export const MINIMUM_AGE = 18;

/** A calendar date with no time and no zone: what a birth date actually is. */
export interface CalendarDate {
  year: number;
  /** 1-12, as a human writes it — NOT a JavaScript month index. */
  month: number;
  day: number;
}

/**
 * Parse `YYYY-MM-DD`, rejecting anything that is not a real date.
 *
 * **Strict, because the browser's date input is not the only caller.** `new
 * Date('2025-02-31')` silently becomes 3 March, so a typo would be accepted and
 * then mean something else. A date that does not exist is refused instead.
 */
export function parseCalendarDate(input: string): CalendarDate | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(input.trim());
  if (!m) return null;

  const year = Number(m[1]);
  const month = Number(m[2]);
  const day = Number(m[3]);
  if (month < 1 || month > 12 || day < 1) return null;
  if (day > daysInMonth(year, month)) return null;

  return { year, month, day };
}

/** Days in a month, leap years included — the reason 29 February is testable. */
export function daysInMonth(year: number, month: number): number {
  if (month === 2) {
    const leap = (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0;
    return leap ? 29 : 28;
  }
  return [4, 6, 9, 11].includes(month) ? 30 : 31;
}

/**
 * Completed years between two calendar dates.
 *
 * "Completed" is the point: somebody is 17 right up to the end of the day
 * before their eighteenth birthday, and 18 on the day itself. Counting the
 * difference in years alone makes them 18 from January of that year.
 *
 * Negative when `born` is after `today` — a date in the future is a mistake,
 * and returning a negative number lets the caller say so rather than hiding it
 * as zero.
 */
export function ageOn(born: CalendarDate, today: CalendarDate): number {
  let age = today.year - born.year;
  const hadBirthday = today.month > born.month
    || (today.month === born.month && today.day >= born.day);
  if (!hadBirthday) age -= 1;
  return age;
}

/**
 * Is this person old enough?
 *
 * **29 February resolves on 1 March in common years**, which falls out of the
 * comparison above rather than being special-cased: on 28 February the month is
 * equal and the day is lower, so the birthday has not happened; on 1 March the
 * month is higher, so it has. That is the rule most of the world's law uses and
 * it needs no code of its own — but it is the case a reader will look for, so
 * it is named here and pinned in the tests.
 */
export function isOldEnough(born: CalendarDate, today: CalendarDate): boolean {
  return ageOn(born, today) >= MINIMUM_AGE;
}

/**
 * The date somebody born on `born` becomes old enough.
 *
 * Shown to a person who is refused, because "come back on 4 May 2027" is
 * something they can act on and "you are too young" is not.
 *
 * **It rolls FORWARD, and the first version clamped backwards.** `Math.min(day,
 * daysInMonth)` turned 29 February into the 28th — a day on which
 * `isOldEnough` still says no. The screen would have named a date, the person
 * would have come back on it, and been refused again. Caught by the test that
 * compares the two functions rather than either alone, which is the only way
 * this kind of disagreement shows up.
 */
export function eighteenthBirthday(born: CalendarDate): CalendarDate {
  const year = born.year + MINIMUM_AGE;
  if (born.day <= daysInMonth(year, born.month)) {
    return { year, month: born.month, day: born.day };
  }
  // Only 29 February reaches here, and only in a common year. The next day
  // that exists is the 1st of the following month — which is exactly the day
  // `isOldEnough` starts saying yes.
  return born.month === 12
    ? { year: year + 1, month: 1, day: 1 }
    : { year, month: born.month + 1, day: 1 };
}

/** `YYYY-MM-DD`, the only format this module reads or writes. */
export function formatCalendarDate(d: CalendarDate): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.year}-${pad(d.month)}-${pad(d.day)}`;
}
